/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import dayjs from 'dayjs';
import { Atom, atom } from 'jotai';
import { activeEventSelector, activeSeriesIdSelector, activeSessionReplayEnabledSelector } from 'jotai/session';
import { atomFamily, unwrap } from 'jotai/utils';
import { DriverPoints, DriverVehicleSeriesData, EventDriverNameVehicleNumber } from 'models/viewModels';
import { driverService } from 'services/driver/DriverService';
import { ManufacturerObject } from '.';
import { getOemColor } from './OemColorObject';

const driversBySeriesSelectorFamily = atomFamily<string, Atom<DriverVehicleSeriesData[] | undefined>>(
  (seriesId: string) =>
    unwrap(
      atom<Promise<DriverVehicleSeriesData[]>>(async (get) => {
        const isRaceReplay = get(activeSessionReplayEnabledSelector);
        const event = get(activeEventSelector);
        const drivers = await driverService.getDrivers(
          seriesId,
          event?.eventId,
          isRaceReplay && event?.eventDate
            ? dayjs(event.eventDate).toDate().getFullYear()
            : dayjs(new Date()).toDate().getFullYear()
        );
        return drivers.sort((a, b) => parseInt(a.vehicleNumber) - parseInt(b.vehicleNumber));
      })
    )
);

export const activeSeriesDriversSelector = atom<DriverVehicleSeriesData[] | undefined>((get) => {
  const activeSeriesId = get(activeSeriesIdSelector);
  return activeSeriesId ? get(driversBySeriesSelectorFamily(activeSeriesId)) : [];
});

const driversByEventGroupIdAtomFamily = atomFamily<string, Atom<DriverVehicleSeriesData[] | undefined>>(
  (eventGroupId: string) =>
    unwrap(
      atom<Promise<DriverVehicleSeriesData[]>>(async () => {
        const drivers = await driverService.getDriversByEventGroupId(eventGroupId);
        return drivers.sort((a, b) => parseInt(a.vehicleNumber) - parseInt(b.vehicleNumber));
      })
    )
);

export const activeEventGroupDriversSelector = atom<DriverVehicleSeriesData[] | undefined>((get) => {
  const eventGroupId = get(activeEventSelector)?.trackEventId || '';
  return eventGroupId ? get(driversByEventGroupIdAtomFamily(eventGroupId)) : [];
});

export const seriesUniqueVehicleNumbersSelectorFamily = atomFamily<string, Atom<string[]>>((seriesId: string) =>
  atom<string[]>((get) => {
    const drivers = get(driversBySeriesSelectorFamily(seriesId));
    const vehicleNumbers = drivers?.map((driver) => driver.vehicleNumber);
    return Array.from(new Set<string>(vehicleNumbers));
  })
);

export const activeSeriesUniqueVehicleNumbersSelector = atom<string[]>((get) => {
  const activeSeriesId = get(activeSeriesIdSelector);
  return activeSeriesId ? get(seriesUniqueVehicleNumbersSelectorFamily(activeSeriesId)) : [];
});

export const oemBySeriesSelector = atom<Promise<ManufacturerObject[]>>(async (get) => {
  const activeSeriesId = get(activeSeriesIdSelector);
  if (activeSeriesId) {
    const oemList = await driverService.getVehicleOemList(activeSeriesId);
    return oemList.map((oem) => getOemColor(oem));
  }
  return [];
});

export const playoffDriverAtomFamily = atomFamily<string, Atom<DriverPoints[] | undefined>>((seriesId: string) =>
  unwrap(
    atom<Promise<DriverPoints[]>>(async (get) => {
      const isRaceReplay = get(activeSessionReplayEnabledSelector);
      const event = get(activeEventSelector);
      return await driverService.getPlayoffDrivers(
        seriesId,
        isRaceReplay && event?.eventDate
          ? dayjs(event.eventDate).toDate().getFullYear()
          : dayjs(new Date()).toDate().getFullYear(),
        event?.eventId
      );
    })
  )
);

export const driverNamesSelector = atom<Promise<EventDriverNameVehicleNumber[]>>(async (get) => {
  const event = get(activeEventSelector);
  if (!event || !event.eventId) {
    console.log('No eventId provided to driverNamesSelector');
    return [];
  }
  return await driverService.getDriversByEventId(event.eventId);
});

export const unwrappedDriverNamesSelector = unwrap(driverNamesSelector);

export const unwrappedOemBySeriesSelector = unwrap(oemBySeriesSelector);

// returns all vehicle numbers for a series for all seasons, used for series image gallery
export const distinctSeriesVehiclesSelectorFamily = atomFamily<number, Atom<DriverVehicleSeriesData[] | undefined>>(
  (seriesId: number) =>
    unwrap(
      atom<Promise<DriverVehicleSeriesData[] | undefined>>(async (_get) => {
        return driverService
          .getDistinctSeriesVehicles(seriesId)
          .then((vehicles) => vehicles.sort((a, b) => parseInt(a.vehicleNumber) - parseInt(b.vehicleNumber)));
      })
    )
);
